import { useContext, useEffect, useMemo, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Tabs, Table, message } from 'antd';
import { styled } from 'styled-components';

import { LayoutContext } from '../../baseLayout/provider';
import {
  deletePanelGoods,
  deleteGoods,
  getCartDataApi,
  getPanelCartDataApi,
  updateChecked,
  updatePanelChecked,
} from '../header-http';

import SvgIcon from '@/components/SvgIcon';
import useUserStore from '@/store/user';
import { buriedPoint } from '@/utils/buriedPoint';
import lcMsgTips from '@/utils/lcMsgTips';
import Utils from '@/utils/Utils';

const CusTableStyles = styled.div`
  /* 改点Table组件的样式 */
  .ant-table {
    color: #54657d;
    font-size: 12px;
    &.ant-table-small {
      .ant-table-thead > tr > th {
        padding: 0 8px;
        height: 28px;
        line-height: 28px;
      }
      .ant-table-body {
        .ant-table-tbody > tr > td {
          padding: 0 8px;
          height: 34px;
          line-height: 34px;
        }
      }
    }
  }
  .ant-table-thead > tr > th::before {
    width: 0 !important;
  }

  .ant-table-tbody > tr.ant-table-row-selected > .ant-table-cell {
    background-color: transparent !important; /* 设置为你想要的颜色，这里设置为透明 */
  }

  .ant-table-wrapper {
    border-radius: 10px;
    overflow: hidden;
  }
  .ant-tabs-nav-list {
    padding-bottom: 3px;
    .ant-tabs-tab {
      padding: 0;
      font-size: 12px;
    }
  }
  .ant-tabs-top > .ant-tabs-nav {
    margin: 0 0 10px 0;
  }
`;

function successFn({ code, msg }) {
  if (code !== 200) {
    lcMsgTips.showConfirmModal({
      title: msg || '操作失败，请前往购物车页面操作',
    });
  }
}
function failFn() {
  lcMsgTips.showConfirmModal({
    title: '操作失败，请前往购物车页面操作',
  });
}

function updateCheck(ids) {
  const ck = ids.join('~') || false;
  const fd = new FormData();
  fd.append('ck', ck);
  updateChecked(fd).then(successFn).catch(failFn);
}

function updatePanelCheck(uuid, isSelected, isSelectedAll) {
  const fd = new FormData();
  if (uuid) {
    fd.append('uuid', uuid);
    fd.append('isSelected', isSelected);
  } else {
    fd.append('isSelectedAll', isSelectedAll);
  }
  updatePanelChecked(fd).then(successFn).catch(failFn);
}

const CartTable = ({
  curSecondTabKey,
  dataSource,
  resourceData,
  getCartData,
  allTabSelectedKeys,
  setAllTabSelectedKeys,
}) => {
  const isPanelShoppingCart = curSecondTabKey === 'cartPanelDataShoppingCart';
  const isHkShoppingCart = curSecondTabKey === 'dollerHkShoppingCart';
  const isOversea = curSecondTabKey === 'shoppingCartOverseaVO';
  const checkedDatas = dataSource.filter((item) => (isPanelShoppingCart ? item.isSelect : item.isChecked));

  // 获取当前tab默认勾选的key
  const defaultKeys = isPanelShoppingCart
    ? dataSource.map((item) => {
        if (item.isSelect) {
          return item.uuid;
        }
        return undefined;
      })
    : allTabSelectedKeys;

  const [totalCount, setTotalCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  const updateCountAndPrice = (datas) => {
    setTotalCount(datas.length);
    setTotalPrice(
      datas.reduce((pre, next) => pre + (isPanelShoppingCart ? next.productTotalMoney : next.lineMoney), 0).toFixed(2)
    );
  };

  useEffect(() => {
    updateCountAndPrice(checkedDatas);
  }, [curSecondTabKey]);

  async function delPanelGoods(item) {
    const data = {
      selectedUuidList: [item?.uuid],
      isSelectAll: false,
    };
    try {
      const { needLogin, code } = await deletePanelGoods(data);
      if (needLogin) {
        return Utils.winLoginFn();
      }
      if (code === 200) {
        message.success('删除成功！');
        getCartData((sizeAndPrice) => {
          setTotalCount(sizeAndPrice.size);
          setTotalPrice(sizeAndPrice.price);
        });
      }
    } catch (e) {
      //
    }
  }

  async function delGoods(item) {
    const data = {
      cartKeyList: [item?.shopCarMapKey],
      isSelectAll: false,
    };
    try {
      const { needLogin, code } = await deleteGoods(data);
      if (needLogin) {
        return Utils.winLoginFn();
      }
      if (code !== 200) {
        return;
      }
      message.success('删除成功！');
      getCartData((sizeAndPrice) => {
        setTotalCount(sizeAndPrice.size);
        setTotalPrice(sizeAndPrice.price);
      });
    } catch (e) {
      //
    }
  }

  const columns = [
    {
      title: '商品编号',
      dataIndex: 'productCode',
      ellipsis: true,
      key: 'productCode',
      width: 80,
      render: (val, record) => {
        return <span className="text-[12px]">{record.selfProductCode || val}</span>;
      },
    },
    {
      title: '型号',
      dataIndex: 'productModel',
      key: 'productModel',
      width: 135,
      ellipsis: true,
    },
    {
      title: '单价（含增值税）',
      dataIndex: 'productConsultPrice',
      key: 'productConsultPrice',
      ellipsis: true,
      width: 90,
      render: (val, { productDiscountPrice }) => {
        return (
          <span className="text-[12px]">
            {isHkShoppingCart ? '$' : '￥'}
            {productDiscountPrice || val}
          </span>
        );
      },
    },
    {
      title: '数量',
      ellipsis: true,
      dataIndex: 'productNumber',
      key: 'productNumber',
      width: 80,
      render: (_, { productNumber, convesionRatio, hkConvesionRatio }) => {
        return (
          <span className="text-[12px]">{productNumber * (isHkShoppingCart ? hkConvesionRatio : convesionRatio)}</span>
        );
      },
    },
    {
      title: '小计',
      dataIndex: 'lineMoney',
      ellipsis: true,
      key: 'lineMoney',
      width: 100,
      render: (val) => {
        return (
          <span className="font-bold text-[#FF4960] text-[12px]">
            {isHkShoppingCart ? '$' : '￥'}
            {val}
          </span>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'age',
      key: 'age',
      render: (_, item) => {
        return (
          <button className="text-[#54657D]" onClick={() => delGoods(item)}>
            删除
          </button>
        );
      },
    },
  ];

  const panelColumns = [
    {
      title: '文件名称',
      dataIndex: 'fileName',
      ellipsis: true,
      key: 'fileName',
      width: 180,
    },
    {
      title: '单价（含增值税）',
      dataIndex: 'productPrice',
      key: 'productPrice',
      ellipsis: true,
      width: 90,
      render: (val) => {
        return <span className="text-[12px]">￥{val}</span>;
      },
    },
    {
      title: '数量',
      ellipsis: true,
      dataIndex: 'customizeNum',
      key: 'customizeNum',
      width: 80,
      render: (_, { panelStyle, customizeNum, printNum }) => {
        return <span className="text-[12px]">{panelStyle ? customizeNum : printNum}</span>;
      },
    },
    {
      title: '小计',
      dataIndex: 'productTotalMoney',
      ellipsis: true,
      key: 'productTotalMoney',
      width: 100,
      render: (val) => {
        return <span className="font-bold text-[#FF4960] text-[12px]">￥{val}</span>;
      },
    },
    {
      title: '操作',
      dataIndex: 'age',
      key: 'age',
      render: (_, item) => {
        return (
          <button className="text-[#54657D]" onClick={() => delPanelGoods(item)}>
            删除
          </button>
        );
      },
    },
  ];

  const rowSelection = {
    defaultSelectedRowKeys: defaultKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      updateCountAndPrice(selectedRows);
    },
    onSelect: (record, selected) => {
      if (isPanelShoppingCart) {
        updatePanelCheck(record.uuid, selected); // 更新面板购物车商品勾选状态
      } else {
        if (selected) {
          setAllTabSelectedKeys([...allTabSelectedKeys, record.shopCarId]);
          updateCheck([...allTabSelectedKeys, record.shopCarId]); // 更新常规购物车商品勾选状态
        } else {
          const ids = allTabSelectedKeys.filter((key) => key !== record.shopCarId);
          setAllTabSelectedKeys(ids);
          updateCheck(ids); // 更新常规购物车商品勾选状态
        }
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (isPanelShoppingCart) {
        updatePanelCheck(null, null, selected); // 更新面板购物车商品勾选状态
      } else {
        if (selected) {
          setAllTabSelectedKeys([...allTabSelectedKeys, ...changeRows.map((row) => row.shopCarId)]);
          updateCheck([...allTabSelectedKeys, ...changeRows.map((row) => row.shopCarId)]); // 更新常规购物车商品勾选状态
        } else {
          const cartIds = changeRows.map((row) => row.shopCarId);
          const ids = allTabSelectedKeys.filter((key) => !cartIds.includes(key));
          setAllTabSelectedKeys(ids);
          updateCheck(ids); // 更新常规购物车商品勾选状态
        }
      }
    },
  };

  const toCartPage =
    resourceData?.projectConfigVO?.lcscCartUrl + (isOversea ? '/cart/display.html?oversea=true' : '/cart/display.html');

  return (
    <>
      <Table
        className="border-solid border-[#d9d9d9] border-[1px]"
        rowKey={isPanelShoppingCart ? 'uuid' : 'shopCarId'}
        rowSelection={rowSelection}
        dataSource={dataSource}
        columns={isPanelShoppingCart ? panelColumns : columns}
        pagination={false}
        size="small"
        style={{ minHeight: 247 }}
        sticky
        scroll={dataSource.length > 6 ? { y: 215 } : null}
        locale={{ emptyText: '暂无商品，赶紧选购吧！' }}
      />
      <ul className="flex items-center justify-end mt-[10px]" data-spm="hd">
        <li className="text-[12px] mr-[10px]">
          <span>商品种类：</span>
          <span className="text-[14px] text-[#ff4960] font-bold">{totalCount}</span>
        </li>
        <li className="text-[12px] mr-[16px]">
          <span>合计：</span>
          <span className="text-[14px] text-[#ff4960] font-bold">{totalPrice}</span>
        </li>
        <li>
          <a
            href={toCartPage}
            className="w-[88px] h-[32px] leading-[32px] bg-[#FF4D4F] rounded-[6px] text-white inline-block text-center hover:bg-[#ff7575] hover:text-white"
            target="_blank"
            data-spm="c3"
          >
            去购物车
          </a>
        </li>
      </ul>
    </>
  );
};
const Empty = ({ loading }) => (
  <section className="h-[350px] pt-[135px]">
    {loading ? (
      <p className="mx-auto text-center text-[36px] text-[#0093e6]">
        <LoadingOutlined spin />
      </p>
    ) : (
      <SvgIcon app="common" iconName="empty-cart" className="w-[60px] h-[50px] mx-auto" />
    )}
    {!loading && <p className="text-center text-[#666] text-[12px] mt-[10px]">购物车中暂无商品，赶紧选购吧！</p>}
  </section>
);
const ShopCarPop = () => {
  const { resourceData } = useContext(LayoutContext);
  /**购物车相关逻辑 */
  const [cartData, setCartData] = useState(null);
  const [panelCartData, setPanelCartData] = useState({});
  const [cartIsEmpty, setCartIsEmpty] = useState(false);
  const [fetchIng, setFetchIng] = useState(false);
  const [curTabKey, setCruTabKey] = useState('normal');

  const { userInfo, updateUserInfo } = useUserStore();

  useEffect(() => {
    try {
      setFetchIng(true);
      getCartData();
    } catch (error) {
      setCartIsEmpty(true);
    }
    buriedPoint({ cp: 'top_header', ty: 'enter', op: 'cart_showing' });
  }, []);

  /**购物车相关逻辑 end*/
  async function getCartData(cb) {
    // 获取购物车
    const cartRes = await getCartDataApi();
    setCartData(cartRes.result);
    updateUserInfo({
      ...userInfo,
      cartNum: cartRes.result?.shoppingCartVO?.cartTotalSize || 0,
    });
    // 获取面板购物车
    const panelCartRes = await getPanelCartDataApi();
    if (!panelCartRes.needLogin) {
      setPanelCartData(panelCartRes.result);
    } else {
      setPanelCartData({
        cartProductList: [],
        selectedTotalCount: 0,
        selectedTotalPrice: 0,
      });
    }
    if (!cartRes.result?.shoppingCartVO?.cartTotalSize) {
      setCruTabKey('panel');
    }
    if (!panelCartRes?.result?.selectedTotalCount) {
      setCruTabKey('normal');
    }
    if (!cartRes?.result?.shoppingCartVO?.cartTotalSize && !panelCartRes?.result?.selectedTotalCount) {
      setCartIsEmpty(true);
      updateUserInfo({
        ...userInfo,
        cartNum: 0,
      });
    }
    setFetchIng(false);

    const sizeAndPrice =
      curTabKey === 'normal'
        ? {
            price: cartRes.result?.shoppingCartVO?.cartSelfMoney || 0,
            size: cartRes.result?.shoppingCartVO?.cartTotalSize || 0,
          }
        : {
            price: panelCartRes.result?.selectedTotalPrice || 0,
            size: panelCartRes.result?.selectedTotalCount || 0,
          };
    cb && cb(sizeAndPrice);
  }

  let tabs = [
    {
      key: 'normal',
      label: `元器件商品 ${cartData?.shoppingCartVO?.cartTotalSize}`,
      children: <NormalTabContent getCartData={getCartData} cartData={cartData} resourceData={resourceData} />,
    },
    {
      key: 'panel',
      label: `面板商品 ${panelCartData?.selectedTotalCount}`,
      children: <PanelTabContent getCartData={getCartData} panelCartData={panelCartData} resourceData={resourceData} />,
    },
  ];

  if (!cartData?.shoppingCartVO?.cartTotalSize) {
    tabs = tabs.filter((t) => t.key !== 'normal');
  }

  if (!panelCartData?.selectedTotalCount) {
    tabs = tabs.filter((t) => t.key !== 'panel');
  }

  const handleTopTabChange = (key) => {
    setCruTabKey(key);
  };

  if (fetchIng) {
    return <Empty loading />;
  }

  return cartIsEmpty ? (
    <Empty />
  ) : (
    <CusTableStyles>
      <ul className="flex items-center mb-[10px]">
        {tabs.map((tab) => {
          let className = 'text-[14px] leading-[22px] mr-[20px] cursor-pointer hover:text-[#0093e6]';
          if (tab.key === curTabKey) {
            className += ' text-[#0093e6]';
          }
          return (
            <li key={tab.key} className={className} onClick={() => handleTopTabChange(tab.key)}>
              <span>{tab.label}</span>
            </li>
          );
        })}
      </ul>
      {tabs.filter((tab) => tab.key === curTabKey)[0]?.children}
    </CusTableStyles>
  );
};

const NormalTabContent = ({ resourceData, cartData, getCartData }) => {
  const overseaAllProductList = useMemo(() => {
    return cartData?.shoppingCartVO?.shoppingCartOverseaVO?.allProductList?.map((t) => t.value)?.flat();
  }, [cartData]);

  // 全部tab页下面的商品
  const allShopGoods = cartData
    ? [
        ...cartData.shoppingCartVO.rmbCnShoppingCart.currentlyProductList,
        ...cartData.shoppingCartVO.rmbCnShoppingCart.isNeedProductList,
        ...cartData.shoppingCartVO.rmbCnShoppingCart.isFreeProductList,
        ...overseaAllProductList,
      ]
    : [];

  // 所有tab勾选的key，面板除外
  const allTabCheckedKeys = allShopGoods.map((item) => {
    if (item.isChecked) {
      return item.shopCarId;
    }
    return undefined;
  });
  const [allTabSelectedKeys, setAllTabSelectedKeys] = useState(allTabCheckedKeys);

  const isFreeProductListSize = cartData?.shoppingCartVO?.rmbCnShoppingCart?.isFreeProductList.length || 0;
  let normalTabs = [
    {
      key: 'rmbCnShoppingCart',
      label: `全部${cartData?.shoppingCartVO?.rmbCnShoppingCart?.productSize + cartData?.shoppingCartVO.shoppingCartOverseaVO.productSize}`,
      children: (
        <CartTable
          curSecondTabKey="rmbCnShoppingCart"
          dataSource={allShopGoods}
          resourceData={resourceData}
          getCartData={getCartData}
          allTabSelectedKeys={allTabSelectedKeys}
          setAllTabSelectedKeys={setAllTabSelectedKeys}
        >
          全部商品
        </CartTable>
      ),
    },
    {
      key: 'currentlyShoppingCart',
      label: `现货商品${cartData?.shoppingCartVO?.currentlyShoppingCart?.productSize + isFreeProductListSize}`,
      children: (
        <CartTable
          curSecondTabKey="currentlyShoppingCart"
          dataSource={[
            ...cartData.shoppingCartVO.rmbCnShoppingCart.currentlyProductList,
            ...cartData.shoppingCartVO.rmbCnShoppingCart.isFreeProductList,
          ]}
          resourceData={resourceData}
          getCartData={getCartData}
          allTabSelectedKeys={allTabSelectedKeys}
          setAllTabSelectedKeys={setAllTabSelectedKeys}
        >
          现货商品
        </CartTable>
      ),
    },
    {
      key: 'isNeedShoppingCart',
      label: `订货商品${cartData?.shoppingCartVO?.isNeedShoppingCart?.productSize + isFreeProductListSize}`,
      children: (
        <CartTable
          curSecondTabKey="isNeedShoppingCart"
          dataSource={[
            ...cartData.shoppingCartVO.rmbCnShoppingCart.isNeedProductList,
            ...cartData.shoppingCartVO.rmbCnShoppingCart.isFreeProductList,
          ]}
          resourceData={resourceData}
          getCartData={getCartData}
          allTabSelectedKeys={allTabSelectedKeys}
          setAllTabSelectedKeys={setAllTabSelectedKeys}
        >
          订货商品
        </CartTable>
      ),
    },
    {
      key: 'shoppingCartOverseaVO',
      label: `代购商品${cartData?.shoppingCartVO?.shoppingCartOverseaVO.productSize}`,
      children: (
        <CartTable
          curSecondTabKey="shoppingCartOverseaVO"
          dataSource={overseaAllProductList}
          resourceData={resourceData}
          getCartData={getCartData}
          allTabSelectedKeys={allTabSelectedKeys}
          setAllTabSelectedKeys={setAllTabSelectedKeys}
        >
          海外代购
        </CartTable>
      ),
    },
  ];

  // 过滤掉没商品的二级tab
  if (
    !cartData?.shoppingCartVO.rmbCnShoppingCart.productSize &&
    !cartData?.shoppingCartVO.shoppingCartOverseaVO.productSize
  ) {
    normalTabs = normalTabs.filter((t) => t.key !== 'rmbCnShoppingCart');
  }
  if (!cartData?.shoppingCartVO.shoppingCartOverseaVO.productSize) {
    normalTabs = normalTabs.filter((t) => t.key !== 'shoppingCartOverseaVO');
  }
  if (!cartData?.shoppingCartVO.currentlyShoppingCart?.productSize) {
    normalTabs = normalTabs.filter((t) => t.key !== 'currentlyShoppingCart');
  }
  if (!cartData?.shoppingCartVO.isNeedShoppingCart?.productSize) {
    normalTabs = normalTabs.filter((t) => t.key !== 'isNeedShoppingCart');
  }

  return (
    <Tabs
      defaultActiveKey="rmbCnShoppingCart"
      destroyInactiveTabPane
      items={normalTabs}
      indicator={{ size: 67, align: 'center' }}
    />
  );
};

const PanelTabContent = ({ resourceData, panelCartData, getCartData }) => {
  return (
    <CartTable
      curSecondTabKey="cartPanelDataShoppingCart"
      dataSource={panelCartData?.cartProductList}
      resourceData={resourceData}
      getCartData={getCartData}
    ></CartTable>
  );
};

export default ShopCarPop;
